import { useSyncExternalStore } from 'react';
import type { StreamContent, AIToolData } from './Types';

type StreamUIState = {
  content: StreamContent;
  isStreaming: boolean;
  streamPhase: string;
  activeSessionId?: string | null;
  awaitingFirstChunk?: boolean;
  lastStreamEventAt?: number | null;
  pendingTextBoundary?: boolean;
  pendingTextBoundaryByKey?: Record<string, boolean>;
  columnOrder?: string[];
  activeRoundKeys?: string[];
  activeRoundCompletedKeys?: string[];
  responseGroupByKey?: Record<string, number>;
  // Stable per-turn column order and tool indices
  turnColumnKeys?: string[];
  taskToolIdByKey?: Record<string, string>;
  toolVmById?: Record<string, AIToolData>;
  // Current backend-declared turn identifier
  currentTurnId?: string;
};

type Listener = () => void;

const listeners = new Set<Listener>();

let state: StreamUIState = {
  content: {
    blocks: [],
    currentPosition: 0,
    totalLength: 0,
  },
  isStreaming: false,
  streamPhase: '',
  activeSessionId: null,
  awaitingFirstChunk: false,
  lastStreamEventAt: null,
  pendingTextBoundary: false,
  pendingTextBoundaryByKey: {},
  columnOrder: [],
  activeRoundKeys: [],
  activeRoundCompletedKeys: [],
  responseGroupByKey: {},
  turnColumnKeys: [],
  taskToolIdByKey: {},
  toolVmById: {},
  currentTurnId: undefined,
};

export function getStreamUIState(): StreamUIState {
  return state;
}

export function setStreamUIState(next: Partial<StreamUIState>) {
  state = { ...state, ...next };
  listeners.forEach((listener) => {
    try { listener(); } catch (_) {}
  });
}

export function resetStreamUIState() {
  state = {
    content: { blocks: [], currentPosition: 0, totalLength: 0 },
    isStreaming: false,
    streamPhase: '',
    activeSessionId: null,
    awaitingFirstChunk: false,
    lastStreamEventAt: null,
  pendingTextBoundary: false,
  pendingTextBoundaryByKey: {},
    columnOrder: [],
    activeRoundKeys: [],
    activeRoundCompletedKeys: [],
    responseGroupByKey: {},
    turnColumnKeys: [],
    taskToolIdByKey: {},
    toolVmById: {},
    currentTurnId: undefined,
  };
  listeners.forEach((listener) => {
    try { listener(); } catch (_) {}
  });
}

// Session helpers
export function setActiveSession(sessionId: string | null) {
  setStreamUIState({ activeSessionId: sessionId });
}

export function setAwaitingFirstChunk(value: boolean) {
  setStreamUIState({ awaitingFirstChunk: value });
}

export function markStreamEventHeartbeat() {
  setStreamUIState({ lastStreamEventAt: Date.now() });
}

export function setPendingTextBoundary(value: boolean) {
  setStreamUIState({ pendingTextBoundary: value });
}

export function setPendingTextBoundaryForKey(key: string | undefined, value: boolean) {
  if (!key) return;
  const current = getStreamUIState();
  const map = { ...(current.pendingTextBoundaryByKey || {}) } as Record<string, boolean>;
  if (map[String(key)] !== !!value) {
    map[String(key)] = !!value;
    setStreamUIState({ pendingTextBoundaryByKey: map });
  }
}

export function takeAndClearPendingBoundaryForKey(key: string | undefined): boolean {
  if (!key) return false;
  const current = getStreamUIState();
  const map = { ...(current.pendingTextBoundaryByKey || {}) } as Record<string, boolean>;
  const has = !!map[String(key)];
  if (has) {
    delete map[String(key)];
    setStreamUIState({ pendingTextBoundaryByKey: map });
  }
  return has;
}

// Response grouping per sub-agent key
export function getResponseGroupForKey(key?: string): number {
  if (!key) return 0;
  const current = getStreamUIState();
  const map = current.responseGroupByKey || {};
  const g = map[String(key)];
  return typeof g === 'number' ? g : 0;
}

export function bumpResponseGroupForKey(key?: string): void {
  if (!key) return;
  const current = getStreamUIState();
  const map = { ...(current.responseGroupByKey || {}) };
  const next = (typeof map[String(key)] === 'number' ? (map[String(key)] as number) + 1 : 1);
  map[String(key)] = next;
  setStreamUIState({ responseGroupByKey: map });
}

// Stable column ordering across a turn
export function registerAgentKey(key?: string) {
  if (!key) return;
  const current = getStreamUIState();
  const order = Array.isArray(current.columnOrder) ? current.columnOrder : [];
  const turnOrder = Array.isArray(current.turnColumnKeys) ? current.turnColumnKeys : [];
  const next: Partial<StreamUIState> = {};
  if (!order.includes(key)) {
    next.columnOrder = [...order, key];
  }
  if (!turnOrder.includes(key)) {
    next.turnColumnKeys = [...turnOrder, key];
  }
  if (next.columnOrder || next.turnColumnKeys) {
    setStreamUIState(next);
  }
}

export function resetColumnOrder() {
  const current = getStreamUIState();
  if (Array.isArray(current.columnOrder) && current.columnOrder.length > 0) {
    setStreamUIState({ columnOrder: [] });
  }
}

// Reset per-turn stable state only (keeps accumulated content)
export function resetTurnState() {
  setStreamUIState({ turnColumnKeys: [], taskToolIdByKey: {}, toolVmById: {} });
}

// Active parallel round helpers
export function beginActiveRound(initialKey?: string) {
  const current = getStreamUIState();
  const hasActive = Array.isArray(current.activeRoundKeys) && current.activeRoundKeys.length > 0;
  if (hasActive) return;
  const nextKeys = initialKey ? [String(initialKey)] : [];
  setStreamUIState({ activeRoundKeys: nextKeys, activeRoundCompletedKeys: [] });
}

export function addKeyToActiveRound(key?: string) {
  if (!key) return;
  const current = getStreamUIState();
  const keys = Array.isArray(current.activeRoundKeys) ? current.activeRoundKeys : [];
  if (!keys.includes(String(key))) {
    setStreamUIState({ activeRoundKeys: [...keys, String(key)] });
  }
}

export function markRoundKeyComplete(key?: string) {
  if (!key) return;
  const current = getStreamUIState();
  const keys = Array.isArray(current.activeRoundKeys) ? current.activeRoundKeys : [];
  const completed = new Set(Array.isArray(current.activeRoundCompletedKeys) ? current.activeRoundCompletedKeys : []);
  completed.add(String(key));
  const completedArr = Array.from(completed);
  setStreamUIState({ activeRoundCompletedKeys: completedArr });
  // If all keys in the active round are completed, clear the round
  const allDone = keys.length > 0 && keys.every(k => completed.has(String(k)));
  if (allDone) {
    setStreamUIState({ activeRoundKeys: [], activeRoundCompletedKeys: [] });
  }
}

export function clearActiveRound() {
  const current = getStreamUIState();
  if ((current.activeRoundKeys && current.activeRoundKeys.length) || (current.activeRoundCompletedKeys && current.activeRoundCompletedKeys.length)) {
    setStreamUIState({ activeRoundKeys: [], activeRoundCompletedKeys: [] });
  }
}

// Tool VM indexing for pinned headers
export function registerTaskToolForKey(key?: string, toolId?: string) {
  if (!key || !toolId) return;
  const current = getStreamUIState();
  const map = { ...(current.taskToolIdByKey || {}) } as Record<string, string>;
  if (map[String(key)] !== String(toolId)) {
    map[String(key)] = String(toolId);
    setStreamUIState({ taskToolIdByKey: map });
  }
}

export function upsertToolVm(vm?: AIToolData) {
  if (!vm || !vm.id) return;
  const current = getStreamUIState();
  const byId = { ...(current.toolVmById || {}) } as Record<string, AIToolData>;
  byId[String(vm.id)] = vm;
  setStreamUIState({ toolVmById: byId });
}

// Turn id helpers
export function setCurrentTurnId(turnId?: string) {
  const current = getStreamUIState();
  if (current.currentTurnId !== (turnId || undefined)) {
    setStreamUIState({ currentTurnId: turnId || undefined });
  }
}

export function getCurrentTurnId(): string | undefined {
  return getStreamUIState().currentTurnId || undefined;
}

export function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useStreamUIState(): StreamUIState {
  return useSyncExternalStore(
    subscribe,
    getStreamUIState,
    getStreamUIState
  );
}


